/**
 * DOC-04 — "بيظهر فوراً بطابور الطبيب المحدد مع إشعار وصوت خفيف".
 *
 * Synthesised rather than shipped as an audio file: two short sine notes are a
 * few lines of WebAudio, they need no asset pipeline, and they stay quiet enough
 * to sit in a treatment room all day. The doctor is holding a tablet, so any
 * sound before the first touch is blocked by the browser's autoplay policy —
 * everything here is best-effort and silent on failure.
 */

type AudioContextCtor = typeof AudioContext;

function resolveCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const legacy = (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  return window.AudioContext ?? legacy ?? null;
}

/** Returns null when the browser has no WebAudio at all. */
export function createAudioContext(): AudioContext | null {
  const Ctor = resolveCtor();
  if (!Ctor) return null;
  try {
    return new Ctor();
  } catch {
    return null;
  }
}

const NOTES = [880, 1174.66]; // A5 → D6, a rising two-note "someone is here".
const NOTE_GAP = 0.13;
const NOTE_LENGTH = 0.26;
const PEAK_GAIN = 0.1; // Deliberately low: this fires beside a patient.

/** Plays the arrival chime. Does nothing unless the context is already running. */
export function playArrivalChime(ctx: AudioContext | null): void {
  if (!ctx || ctx.state !== 'running') return;

  try {
    const start = ctx.currentTime;
    for (const [index, frequency] of NOTES.entries()) {
      const at = start + index * NOTE_GAP;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, at);

      // An exponential ramp cannot touch zero, hence the near-silent floor; the
      // envelope is what keeps this a soft chime instead of a beep with a click.
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + NOTE_LENGTH);

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(at);
      oscillator.stop(at + NOTE_LENGTH + 0.02);
    }
  } catch {
    // A blocked or closed context must never break the queue render.
  }
}
